import JSZip from "jszip";
import security from "@/main";
import { getApiBaseUrl } from "@/config";
import Img from "@/modules/img/components/service/Img";
import { pickRendition } from "@/modules/img/components/service/Renditions";

const EXTENSIONS = {
    "image/jpeg": "jpg", "image/png": "png", "image/gif": "gif", "image/webp": "webp",
    "application/pdf": "pdf", "image/heic": "heic"
};

const withExtension = (name, contentType) => {
    const extension = EXTENSIONS[contentType];
    return !extension || name.toLowerCase().endsWith("." + extension) ? name : name + "." + extension;
};

/** Makes the names in a ZIP unique: a second "a.jpg" becomes "a (2).jpg". */
const uniqueName = (used, name) => {
    let candidate = name;
    for (let n = 2; used.has(candidate); n++) {
        const dot = name.lastIndexOf(".");
        candidate = dot > 0 ? name.slice(0, dot) + " (" + n + ")" + name.slice(dot) : name + " (" + n + ")";
    }
    used.add(candidate);
    return candidate;
};

/** Loads the largest viewable rendition of each image and zips them. */
async function zipMedia (images) {
    const zip = new JSZip();
    const used = new Set();
    for (const image of images) {
        const rendition = pickRendition(image.renditions, "full")
            || (image.renditions || []).find(r => r.name === "original");
        if (!rendition) {
            continue;
        }

        const response = await fetch(getApiBaseUrl() + rendition.url, { method: "GET", headers: security.jsonHeader() });
        if (!response.ok) {
            return Promise.reject(response);
        }
        const name = withExtension(String(image.name || image.fileName || image.id), rendition.contentType);
        zip.file(uniqueName(used, name), await response.blob());
    }
    return zip.generateAsync({ type: "blob" });
}

export default {
    /** ZIP of all [images] of a folder (loaded in the browser), as an object URL; null if there are none. */
    async zip (images) {
        return images.length > 0 ? URL.createObjectURL(await zipMedia(images)) : null;
    },

    /** Downloads one image of a folder's images (full size). */
    async image (image) {
        const url = Img.imageUrl(image, "full");
        if (!url) {
            return Promise.reject();
        }
        const response = await fetch(url, { method: "GET", headers: security.jsonHeader() });
        if (!response.ok) {
            return Promise.reject(response);
        }
        const blob = await response.blob();
        const name = withExtension(String(image.name || image.fileName || image.id), blob.type);
        this.save(URL.createObjectURL(blob), name);
    },

    /** Saves [url] (an object URL) as a file called [filename]. */
    save (url, filename) {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
    }
};
